import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import debounce from 'lodash.debounce';

const UnifiedSearch = ({ isCompact = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [proteinResults, setProteinResults] = useState(null);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Function to detect if the search term might be a protein name
  const mightBeProteinName = (term) => {
    return /^[A-Za-z0-9\-]+$/.test(term) &&
      !term.match(/^[0-9]+$/) && // Not just a number
      term.length >= 3;
  };

  // Debounced autocomplete function that fetches from all entity types
  const fetchAutocompleteSuggestions = useCallback(
    debounce(async (term) => {
      if (!term || term.length < 2) {
        setAutocompleteResults([]);
        setShowAutocomplete(false);
        return;
      }

      setIsLoading(true);
      try {
        // First fetch regular autocomplete results
        const response = await fetch(
          `/api/autocomplete?q=${encodeURIComponent(term)}&type=all`
        );
        const data = await response.json();
        let results = data.success ? [...data.data] : [];

        // Extract gene names already in results to avoid duplicates
        const existingGeneNames = results
          .filter(item => item.entityType === 'gene')
          .map(item => item.text.toUpperCase());

        // If this might be a protein name, check for protein matches
        if (mightBeProteinName(term)) {
          try {
            const proteinResponse = await fetch(`/api/protein/lookup?name=${encodeURIComponent(term)}`);
            const proteinData = await proteinResponse.json();

            if (proteinData.success && proteinData.data.length > 0) {
              // Store all found protein genes
              setProteinResults({
                genes: proteinData.data,
                originalTerm: term
              });

              // Filter out genes that already exist in autocomplete results (case insensitive)
              const uniqueProteinGenes = proteinData.data.filter(
                geneName => !existingGeneNames.includes(geneName.toUpperCase())
              );

              // Add unique genes from protein search with a note
              const proteinGenesSuggestions = uniqueProteinGenes.map(geneName => ({
                text: geneName,
                type: 'Alias',
                entityType: 'gene',
                // description: `Protein: ${term}`
              }));

              // Add unique protein gene suggestions to results
              if (proteinGenesSuggestions.length > 0) {
                results = [...proteinGenesSuggestions, ...results];
              }
            } else {
              setProteinResults(null);
            }
          } catch (error) {
            console.error('Protein lookup failed:', error);
          }
        }

        setAutocompleteResults(results);
        setShowAutocomplete(results.length > 0);
      } catch (error) {
        console.error('Autocomplete fetch failed:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    fetchAutocompleteSuggestions(term);
  };

  const handleAutocompleteClick = (suggestion) => {
    // Handle each entity type
    const currentPath = window.location.pathname;

    if (suggestion.entityType === 'gene') {
      // If already on gene search page, force a page reload with the new search term
      if (currentPath === '/gene-search') {
        window.location.href = `/gene-search?q=${encodeURIComponent(suggestion.text)}`;
      } else {
        navigate(`/gene-search?q=${encodeURIComponent(suggestion.text)}`);
      }
    } else if (suggestion.entityType === 'disease') {
      // If already on disease search page, force a page reload
      if (currentPath === '/disease-search') {
        window.location.href = `/disease-search?q=${encodeURIComponent(suggestion.text)}`;
      } else {
        navigate(`/disease-search?q=${encodeURIComponent(suggestion.text)}`);
      }
    } else if (suggestion.entityType === 'paper') {
      // If already on paper search page, force a page reload
      if (currentPath === '/paper-search') {
        window.location.href = `/paper-search?q=${encodeURIComponent(suggestion.text)}`;
      } else {
        navigate(`/paper-search?q=${encodeURIComponent(suggestion.text)}`);
      }
    }

    // Clear search after navigating
    setSearchTerm('');
    setShowAutocomplete(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchTerm) return;

    const currentPath = window.location.pathname;
    const isOnGeneSearch = currentPath === '/gene-search';

    // If this might be a protein name, first check if we have protein results
    if (mightBeProteinName(searchTerm) && !proteinResults) {
      // Check for protein matches before navigating
      checkForProteinMatches(searchTerm);
    } else if (proteinResults && proteinResults.genes.length > 0) {
      // We have protein results, search the first gene directly
      if (isOnGeneSearch) {
        window.location.href = `/gene-search?q=${encodeURIComponent(proteinResults.genes[0])}`;
      } else {
        navigate(`/gene-search?q=${encodeURIComponent(proteinResults.genes[0])}`);
      }
    } else {
      // Regular search - try gene search first
      if (isOnGeneSearch) {
        window.location.href = `/gene-search?q=${encodeURIComponent(searchTerm)}`;
      } else {
        navigate(`/gene-search?q=${encodeURIComponent(searchTerm)}`);
      }
    }

    // Clear search after navigating
    setSearchTerm('');
    setShowAutocomplete(false);
    setProteinResults(null);
  };

  // Function to check if a term is a protein name
  const checkForProteinMatches = async (term) => {
    try {
      const response = await fetch(`/api/protein/lookup?name=${encodeURIComponent(term)}`);
      const data = await response.json();

      const currentPath = window.location.pathname;
      const isOnGeneSearch = currentPath === '/gene-search';

      if (data.success && data.data.length > 0) {
        // Found protein matches, navigate to gene search with the first gene from results
        if (isOnGeneSearch) {
          window.location.href = `/gene-search?q=${encodeURIComponent(data.data[0])}`;
        } else {
          navigate(`/gene-search?q=${encodeURIComponent(data.data[0])}`);
        }
      } else {
        // No protein matches, do regular search
        if (isOnGeneSearch) {
          window.location.href = `/gene-search?q=${encodeURIComponent(term)}`;
        } else {
          navigate(`/gene-search?q=${encodeURIComponent(term)}`);
        }
      }
    } catch (error) {
      console.error('Protein lookup failed:', error);
      // Fall back to regular search
      const isOnGeneSearch = window.location.pathname === '/gene-search';
      if (isOnGeneSearch) {
        window.location.href = `/gene-search?q=${encodeURIComponent(term)}`;
      } else {
        navigate(`/gene-search?q=${encodeURIComponent(term)}`);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setAutocompleteResults([]);
    setShowAutocomplete(false);
    setProteinResults(null);
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [autocompleteRef, inputRef]);

  // Highlight matching terms in autocomplete results
  const highlightText = (text, term) => {
    if (!term || !text) return text;

    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const escapedTerm = escapeRegExp(term);
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    const parts = text.toString().split(regex);

    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="bg-blue-100 text-blue-900 px-0.5 rounded font-medium">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };

  // Get the badge color based on entity type
  const getBadgeColor = (entityType) => {
    switch (entityType) {
      case 'gene':
        return 'bg-blue-100 text-blue-700';
      case 'disease':
        return 'bg-purple-100 text-purple-700';
      case 'paper':
        return 'bg-indigo-100 text-indigo-700';
      case 'protein':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className={`${isCompact ? 'w-full' : 'max-w-2xl mx-auto'} relative`}>
      <form onSubmit={handleSearchSubmit} className="flex">
        <div className="flex-1 relative">
          <div className={`absolute inset-y-0 left-0 ${isCompact ? 'pl-2' : 'pl-3'} flex items-center pointer-events-none`}>
            <Search className={`${isCompact ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} />
          </div>
          <input
            ref={inputRef}
            type="text"
            className={`block w-full ${isCompact
              ? 'pl-8 pr-8 py-2 text-sm h-10 rounded-md'
              : 'pl-10 pr-10 py-3 text-lg h-14 rounded-l-md'
              } border border-gray-300 leading-5 bg-white ${isCompact ? 'focus:ring-1' : 'shadow-sm focus:ring-2'
              } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
            placeholder={isCompact ? "Search..." : "Search genes, diseases, or synapse proteomic publications..."}
            value={searchTerm}
            onChange={handleInputChange}
            onClick={() => {
              if (searchTerm && searchTerm.length >= 2) {
                setShowAutocomplete(true);
              }
            }}
          />
          {searchTerm && (
            <button
              type="button"
              className={`absolute inset-y-0 right-0 ${isCompact ? 'pr-2' : 'pr-3'} flex items-center`}
              onClick={clearSearch}
            >
              <X className={`${isCompact ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400 hover:text-gray-500`} />
            </button>
          )}
        </div>
        {!isCompact && (
          <button
            type="submit"
            className="px-6 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 text-white rounded-r-md hover:bg-gradient-to-br hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-lg h-14"
          >
            Search
          </button>
        )}
      </form>

      {showAutocomplete && (
        <div
          ref={autocompleteRef}
          className={`absolute z-50 w-full bg-white mt-1 rounded-md shadow-lg ${isCompact ? 'max-h-64' : 'max-h-96'
            } overflow-auto border border-gray-200`}
        >
          {isLoading ? (
            <div className="px-4 py-3 text-center text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : (
            <ul className="py-1">
              {autocompleteResults.length > 0 ? (
                autocompleteResults.map((suggestion, index) => (
                  <li
                    key={index}
                    className={`${isCompact ? 'px-3 py-2' : 'px-4 py-3'} hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0`}
                    onClick={() => handleAutocompleteClick(suggestion)}
                  >
                    <div className="flex justify-between items-center">
                      <span className={`truncate font-medium ${isCompact ? 'text-sm' : ''}`}>
                        {suggestion.entityType === 'gene' ? (
                          <span className="italic">
                            {highlightText(suggestion.text, searchTerm)}
                          </span>
                        ) : (
                          highlightText(suggestion.text, searchTerm)
                        )}
                      </span>
                      <span className={`${isCompact ? 'text-xs' : 'text-xs'} px-2 py-1 rounded-full ml-2 whitespace-nowrap ${getBadgeColor(suggestion.entityType)}`}>
                        {suggestion.type}
                      </span>
                    </div>
                    {suggestion.entityType === 'protein' && !isCompact && (
                      <div className="text-sm text-green-600 mt-1">
                        Related genes: {suggestion.genes.join(', ')}
                      </div>
                    )}
                    {suggestion.description && !isCompact && (
                      <div className="text-sm text-gray-500 mt-1 truncate">
                        {suggestion.description}
                      </div>
                    )}
                    {suggestion.title && suggestion.title !== suggestion.text && !isCompact && (
                      <div className="text-sm text-gray-500 mt-1 truncate">
                        {suggestion.title}
                      </div>
                    )}
                  </li>
                ))
              ) : (
                <li className={`${isCompact ? 'px-3 py-2 text-sm' : 'px-4 py-3'} text-gray-500`}>
                  No matches found. Try another search term.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedSearch;